<template>
  <default-layout class="people-home">
    <main-container size="large">
      <div v-if="$screen.isTabletOrMobile($mq)" class="people-home__header">
        <div>
          <h2>Users</h2>
        </div>
        <div>
          <dd-button
            :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
            color="blue" 
            size="small"
            new-icon
            @click="goToCreatePeople()"
          >
            Invite New User
          </dd-button>
        </div>
      </div>
      <div v-else :class="{'with-unregistered-users': hasMultipleUnregisteredUsers}" class="people-home__header">
        <div>
          <h2>Users</h2>
          <sleek-filter-input  
            v-model="searchQuery" 
            class="filter-input" 
            placeholder="Filter by company, person, or email..."
          />
        </div>
        <div>
          <dd-button
            :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
            type="flat"
            size="medium"
            new-icon
            @click="goToCreateCompany()"
          >Create Company</dd-button>
          <dd-button
            :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
            color="blue"
            size="medium"
            new-icon
            @click="goToCreatePeople()"
          >Invite User</dd-button>
        </div>
        <!-- Bulk registration invitation -->
        <div 
          v-if="hasMultipleUnregisteredUsers && !bulkInvitationsAttempted" 
          class="unregistered-users-button" 
          @click="handleInviteUnregisteredUsers"
        >
          <span v-if="isSendingBulkInvitations">Sending...</span>
          <template v-else>
            <i class="ddi ddi-email" />
            <span>Send invite to {{ unregisteredUsersCount }} unregistered users?</span>
          </template>
        </div>
      </div>
      <el-card class="people-home__card">
        <loading-placeholder v-if="isLoading" :lines="20" class="loading" />
        <template v-else>
          <div v-if="noFilterMatches" class="no-filter-matches">
            No filter matches
          </div>
          <!-- Company roster -->
          <div v-for="company in filteredCompanyRosters" :key="company.id" class="people-home__company-roster">
            <div class="header">
              <h3>{{ company.name }} <span class="number-of-people">{{company.numberOfPeople }}</span></h3>
              <more-button-dropdown v-if="!$screen.isMobile($mq)" class="more-button">
                <template #options-list>
                  <ul>
                    <li @click="RouteAssistant.goToCompanyDetail($router, accountID, company.id)">
                      <i class="ddi ddi-edit"/>
                      <span>Edit Company</span>
                    </li>
                    <li v-if="noPeopleInCompany(company)" class="scary-list-option" @click="deleteCompany(company.id)">
                      <i class="ddi ddi-trash" />
                      <span>Delete Company</span>
                    </li>
                  </ul>
                </template>
              </more-button-dropdown>
            </div>
            <people-roster 
              :company="company" 
              @account-memberships-updated="fetchCompanyRosters"
              @person-deleted="fetchCompanyRosters"
              @account-role-update-disallowed="fetchCompanyRosters"
            />
          </div>
        </template>
      </el-card>
    </main-container>
  </default-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import { cloneDeep, debounce } from 'lodash-es'

/* Mixins */

/* Components */
import SleekFilterInput from '@/components/global/inputs/sleek-filter-input.vue'
import PeopleRoster from './components/people-roster.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { ICompanyRoster, IItemForSelection } from '@/api/types/response.types'

/* API */
import CompaniesAPI from '@/api/modules/companies.api'

export default defineComponent({
  name: 'PeopleHome',

  components: {
    DefaultLayout,
    MainContainer,
    SleekFilterInput,
    PeopleRoster,
  },

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSendingBulkInvitations: false,
      bulkInvitationsAttempted: false,
      unregisteredUsersCount: 0,
      debounceFilterCompanyRosters: null as any,
      RouteAssistant,
      searchQuery: '',
      companyRosters: [] as ICompanyRoster[],
      filteredCompanyRosters: [] as ICompanyRoster[],
    }
  },

  head: {
    title: 'Users',
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    companies(): IItemForSelection[] {
      return this.companyRosters.map(c => ({
        id: c.id,
        name: c.name,
      }))
    },
    noFilterMatches(): boolean {
      // We will never have a case where an acct has no companies,
      // so if filtered companies length is 0, we know it's due to no filter matches.
      return this.filteredCompanyRosters.length === 0
    },
    hasSearchQuery(): boolean {
      return this.searchQuery.length > 0
    },
    hasMultipleUnregisteredUsers(): boolean {
      return this.unregisteredUsersCount > 1
    },
  },

  watch: {
    searchQuery: {
      immediate: false,
      handler() {
        this.debounceFilterCompanyRosters()
      },
    },
  },

  async created() {
    this.debounceFilterCompanyRosters = debounce(this.filterCompanyRosters, 300)
    this.isLoading = true
    await this.fetchCompanyRosters()
    this.isLoading = false
  },

  methods: {
    noPeopleInCompany(company: ICompanyRoster): boolean {
      return company.numberOfPeople === 0
    },
    filterCompanyRosters(): void {
      if (!this.hasSearchQuery) {
        this.filteredCompanyRosters = this.companyRosters
        return
      }
      this.filteredCompanyRosters = []
      this.companyRosters.forEach(company => {
        // Company match...
        if (
          company.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        ) {
          this.filteredCompanyRosters.push(company)
        } else {
          // People match...
          const peopleMatches = company.listPeople.filter(person => {
            return (
              person.name
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase()) ||
              person.email
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase())
            )
          })

          const curCompany: ICompanyRoster = cloneDeep(company)
          curCompany.listPeople = peopleMatches

          if (peopleMatches.length > 0) {
            this.filteredCompanyRosters.push(curCompany)
          }
        }
      })
    },
    async goToCreateCompany() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreateCompany(this.$router, this.accountID)
    },
    async goToCreatePeople() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      RouteAssistant.goToCreatePeople(this.$router, this.accountID)
    },
    async fetchCompanyRosters() {
      try {
        this.companyRosters = await CompaniesAPI.get_Rosters()
        this.unregisteredUsersCount = this.companyRosters
          .map(company => company.listPeople) // Extract all people arrays
          .reduce((acc, people) => acc.concat(people), []) // Flatten the array of people
          .filter(person => !person.hasRegistered).length // Filter people with hasRegistered == true

        this.filterCompanyRosters()
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async deleteCompany(id: number) {
      try {
        const response = await CompaniesAPI.delete_Delete(id)
        this.$messaging.showSuccess(response.message)
        this.isLoading = true
        await this.fetchCompanyRosters()
        this.isLoading = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
    handleInviteUnregisteredUsers() {
      this.$confirm(
        `They'll receive an email with a link and instructions to complete their registration.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Send invites',
          confirmButtonClass: 'el-button--primary',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Send invites to ${
            this.unregisteredUsersCount
          } unregistered users?`,
        },
      ).then(() => {
        this.inviteUnregisteredUsers()
      })
    },
    async inviteUnregisteredUsers() {
      try {
        this.isSendingBulkInvitations = true
        const response = await CompaniesAPI.get_BulkRegistrationInvitation()
        this.$messaging.showSuccess(response.message)
        this.isSendingBulkInvitations = false
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.bulkInvitationsAttempted = true
      }
    },
  },
})
</script>

<style lang="scss">
.people-home {
  &__header {
    position: relative;
    display: flex;
    justify-content: space-between;
    padding-bottom: 14px;

    &.with-unregistered-users {
      padding-bottom: 30px;
    }

    @include media-breakpoint-down(sm) {
      margin-left: 20px;
      margin-right: 20px;
    }

    > div {
      display: flex;
      align-items: center;

      > :not(:last-child) {
        margin-right: 10px;
      }

      .filter-input {
        margin-left: 10px !important;
      }
    }

    .unregistered-users-button {
      position: absolute;
      left: 50%;
      bottom: -16px;
      transform: translateX(-50%);
      border: $border--solid-light;
      border-radius: 20px;
      background-color: $color--gray-75;
      padding: 4px 20px;
      width: fit-content;
      @include lettering--p-sm;
      color: $color--white;
      font-weight: $bold;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      @include flex-row-horizontal-spacing(4px);

      &:hover {
        background-color: $color--gray-90;
      }
    }
  }

  &__card {
    overflow: visible;
    padding: 0 20px 40px 20px;

    @include media-breakpoint-down(sm) {
      padding-left: 5px;
      padding-right: 5px;

      &.el-card {
        border-radius: 0 !important;
      }
    }

    .loading {
      padding-top: 20px;
    }

    .no-filter-matches {
      padding-top: 50px;
      display: flex;
      justify-content: center;
      color: var(--color-text-third);
    }
  }

  &__company-roster {
    &:not(:first-child) {
      padding-top: 30px;
    }

    .header {
      display: flex;
      align-items: center;
      border-bottom: solid 2px $color--gray-15;
      width: calc(100% + 72px);
      left: -36px;
      position: relative;
      padding: 2px 36px 14px 36px;

      .more-button {
        margin-left: 10px;
      }

      @include media-breakpoint-down(md) {
        padding-bottom: 12px;
      }

      > h3 {
        margin: 0;
      }

      .number-of-people {
        padding-left: 6px;
        @include lettering--p;
        color: var(--color-text-third);
      }
    }
  }
}
</style>
